/**
 * BadgeReveal — sparkle-in badge unlock animation
 * Sequence: converging sparkles → badge scale-in with bounce → stamp ring expands
 */

import { type ReactNode } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { springs } from '../../animation';
import { useReducedMotion } from '../../hooks/useReducedMotion';

interface BadgeRevealProps {
  trigger: boolean;
  children: ReactNode;
}

export default function BadgeReveal({ trigger, children }: BadgeRevealProps) {
  const reduced = useReducedMotion();

  if (!trigger) {
    return <>{children}</>;
  }

  if (reduced) {
    return <>{children}</>;
  }

  return (
    <div className="relative inline-flex items-center justify-center">
      <AnimatePresence>
        {/* Converging sparkles */}
        {[0, 1, 2, 3, 4, 5].map((i) => {
          const angle = (i / 6) * Math.PI * 2;
          return (
            <motion.span
              key={`sparkle-${i}`}
              className="absolute pointer-events-none text-kid-dandelion"
              style={{ fontSize: 12 }}
              initial={{
                x: Math.cos(angle) * 40,
                y: Math.sin(angle) * 40,
                opacity: 1,
                scale: 0.5,
              }}
              animate={{
                x: 0,
                y: 0,
                opacity: 0,
                scale: 0,
              }}
              transition={{ duration: 0.4, delay: i * 0.05 }}
            >
              ✦
            </motion.span>
          );
        })}
      </AnimatePresence>

      {/* Badge content */}
      <motion.div
        initial={{ scale: 0, rotate: -20 }}
        animate={{ scale: 1, rotate: 0 }}
        transition={springs.bouncy}
      >
        {children}
      </motion.div>

      {/* Stamp ring */}
      <motion.div
        className="absolute inset-0 rounded-full pointer-events-none"
        style={{ border: '2px solid var(--kid-dandelion)' }}
        initial={{ scale: 0.5, opacity: 0.8 }}
        animate={{ scale: 1.8, opacity: 0 }}
        transition={{ duration: 0.6, delay: 0.3, ease: 'easeOut' }}
      />
    </div>
  );
}
